/**
 * This is the preferred table component over table-basic-legacy as it leverages shadcn's table components
 * and provides a clean abstraction rather than recreating table primitives from scratch.
 * It offers better maintainability and consistency with our design system.
 */
import type * as React from 'react'

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/atomic/table-basic/base'
import { BodyXS } from '@/components/atomic/text'
import { cn } from '@/utils/shadcn/utils'

export interface TableColumn<T> {
  key: string
  header: string
  cellRenderer: (row: T) => React.ReactNode
  align?: 'right' | 'left' | 'center'
}

interface TableBasicProps<T> {
  columns: TableColumn<T>[]
  data: T[]
  className?: string
  getRowClassName?: (row: T) => string
}

function TableBasic<T>({
  columns,
  data,
  className,
  getRowClassName,
}: TableBasicProps<T>) {
  return (
    <Table className={className}>
      <TableHeader>
        <TableRow className="border-b border-primary-border">
          {columns.map((column) => (
            <TableHead
              key={column.key}
              className={cn(
                column.align === 'right' && 'text-right',
                column.align === 'center' && 'text-center',
                column.align === 'left' && 'text-left',
              )}
            >
              <BodyXS>{column.header}</BodyXS>
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.map((row, rowIndex) => (
          <TableRow
            key={rowIndex}
            className={cn(
              'border-b border-primary-border cursor-pointer',
              // biome-ignore lint/complexity/useOptionalChain: Bulk suppression as part of biome migration
              getRowClassName && getRowClassName(row),
            )}
          >
            {columns.map((column) => (
              <TableCell
                key={`${rowIndex}-${column.key}`}
                className={cn(
                  column.align === 'right' && 'text-right',
                  column.align === 'center' && 'text-center',
                  column.align === 'left' && 'text-left',
                )}
              >
                {column.cellRenderer(row)}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default TableBasic
